import {pageParamsQuery} from '@seed-hypermedia/client/page-input'
import type {BlockRange, HMComment, UnpackedHypermediaId} from '@seed-hypermedia/client/hm-types'
import {
  parseCustomURL as _parseCustomURL,
  parseFragment as _parseFragment,
  unpackHmId as _unpackHmId,
} from '@seed-hypermedia/client/hm-types'
import {DEFAULT_GATEWAY_URL, HYPERMEDIA_SCHEME, OS_PROTOCOL_SCHEME} from '../constants'
import type {NavRoute} from '../routes'
import {entityQueryPathToHmIdPath} from './path-api'
import type {StateStream} from './stream'

/**
 * Activity filter slug <-> filterEventType mapping for URL encoding
 */
const ACTIVITY_FILTER_SLUGS: Record<string, string[]> = {
  comments: ['Comment'],
  versions: ['Ref'],
  citations: ['comment/Embed', 'doc/Embed', 'doc/Link', 'doc/Button', 'metadata/Link'],
}

export function activityFilterToSlug(filterEventType?: string[]): string | null {
  if (!filterEventType?.length) return null
  for (const [slug, types] of Object.entries(ACTIVITY_FILTER_SLUGS)) {
    if (types.length === filterEventType.length && types.every((t, i) => t === filterEventType[i])) {
      return slug
    }
  }
  return null
}

export function activitySlugToFilter(slug: string): string[] | undefined {
  return ACTIVITY_FILTER_SLUGS[slug]
}

export const SITE_PROFILE_TABS = ['profile', 'membership', 'followers', 'following'] as const
export type SiteProfileTab = (typeof SITE_PROFILE_TABS)[number]

const SITE_PROFILE_VIEW_TERMS = [':profile', ':membership', ':followers', ':following'] as const
type SiteProfileViewTerm = (typeof SITE_PROFILE_VIEW_TERMS)[number]

// View terms for URL paths (e.g., /:activity, /:directory)
// ':discussions' and ':comment' kept for backward compat URL parsing
export const VIEW_TERMS = [
  ':activity',
  ':comments',
  ':comment',
  ':discussions',
  ':collaborators',
  ':directory',
  ':feed',
  ':all-documents',
  ':explore',
  ':settings',
  ':attributes',
  ':metadata', // backward compat: the metadata view is now surfaced as :attributes
  ':schema', // the schema a document defines (its schemaDefinition), in the schema browser
  ...SITE_PROFILE_VIEW_TERMS,
] as const
export type ViewTerm = (typeof VIEW_TERMS)[number]

/**
 * Whether a path or URL addresses a view-term panel (`/:followers`, `/:comments`, ...)
 * rather than a document itself. Panels are alternate views of documents and accounts
 * whose URL space grows with every pair of accounts, so crawlers are kept out of them
 * (robots.txt) and links to them carry rel="nofollow".
 */
export function isViewTermPath(pathOrUrl: string): boolean {
  let path = pathOrUrl
  const schemeEnd = path.indexOf('://')
  if (schemeEnd >= 0) {
    const pathStart = path.indexOf('/', schemeEnd + 3)
    path = pathStart >= 0 ? path.slice(pathStart) : '/'
  }
  path = path.split(/[?#]/)[0] || ''
  return path.split('/').some((segment) => (VIEW_TERMS as readonly string[]).includes(segment))
}

// Route keys that correspond to view terms (excludes 'options' which is panel-only)
export type ViewRouteKey =
  | 'activity'
  | 'comments'
  | 'collaborators'
  | 'directory'
  | 'feed'
  | 'all-documents'
  | 'explore'
  | 'site-settings'
  | 'metadata'
  | 'schema'
  | SiteProfileTab

// Panel keys that can be encoded in URL query param
export type PanelQueryKey = 'activity' | 'comments' | 'collaborators' | 'directory' | 'options' | 'feed'

function extractInspectPrefixFromUrl(url: string): {url: string; isInspect: boolean} {
  const hmInspectMatch = url.match(/^(hm:\/\/)inspect(?:\/(.*))?$/)
  if (hmInspectMatch) {
    return {url: `${hmInspectMatch[1]}${hmInspectMatch[2] || ''}`, isInspect: true}
  }

  const gatewayInspectMatch = url.match(/^((?:https?:\/\/[^/]+)?\/hm)\/inspect(?=\/|[?#]|$)(.*)$/)
  if (gatewayInspectMatch) {
    return {url: `${gatewayInspectMatch[1]}${gatewayInspectMatch[2] || ''}`, isInspect: true}
  }

  const siteInspectMatch = url.match(/^((?:https?:\/\/[^/]+)?)\/inspect(?=\/|[?#]|$)(.*)$/)
  if (siteInspectMatch) {
    return {url: `${siteInspectMatch[1]}${siteInspectMatch[2] || ''}` || '/', isInspect: true}
  }

  return {url, isInspect: false}
}

/**
 * Extract view term from URL path and return cleaned URL + view term
 * e.g., "https://example.com/path/:directory?v=123" -> {url: "https://example.com/path?v=123", viewTerm: ":directory"}
 */
export function extractViewTermFromUrl(url: string): {
  url: string
  isInspect: boolean
  viewTerm: ViewTerm | null
  activityFilter?: string
  feedTab?: string
  commentId?: string
  accountUid?: string
  settingsTab?: string
  settingsDomain?: string
} {
  const {url: inspectCleanUrl, isInspect} = extractInspectPrefixFromUrl(url)

  // Check for the :feed/to-you pattern (the Activity page on its To you tab)
  const feedTabMatch = inspectCleanUrl.match(/\/\:feed\/(to-you)(?=[?#]|$)/)
  if (feedTabMatch) {
    return {
      url: inspectCleanUrl.replace(feedTabMatch[0], ''),
      isInspect,
      viewTerm: ':feed',
      feedTab: feedTabMatch[1],
    }
  }

  // Check for :comments/<comment-id> or :comment/<comment-id> pattern.
  // Comment IDs may be a single segment or a slash-delimited path.
  const commentsPattern = /\/\:comments?\/([^?#]+?)(?=[?#]|$)/
  const commentsMatch = inspectCleanUrl.match(commentsPattern)
  if (commentsMatch) {
    return {
      url: inspectCleanUrl.replace(commentsMatch[0], ''),
      isInspect,
      viewTerm: ':comments',
      commentId: commentsMatch[1],
    }
  }

  // Check for :activity/<slug> pattern
  const activitySlugPattern = /\/\:activity\/([a-z]+)(?=[?#]|$)/
  const activitySlugMatch = inspectCleanUrl.match(activitySlugPattern)
  if (activitySlugMatch) {
    return {
      url: inspectCleanUrl.replace(activitySlugMatch[0], ''),
      isInspect,
      viewTerm: ':activity',
      activityFilter: activitySlugMatch[1],
    }
  }

  // A custom-domain setup has its own reloadable settings destination.
  const domainSetupMatch = inspectCleanUrl.match(/\/\:settings\/web-domain\/([^/?#]+)(?=[?#]|$)/)
  if (domainSetupMatch) {
    let settingsDomain: string
    try {
      settingsDomain = decodeURIComponent(domainSetupMatch[1]!)
    } catch {
      return {url: inspectCleanUrl, isInspect, viewTerm: null}
    }
    return {
      url: inspectCleanUrl.replace(domainSetupMatch[0], ''),
      isInspect,
      viewTerm: ':settings',
      settingsTab: 'web-domain',
      settingsDomain,
    }
  }

  // Check for site settings patterns like /:settings or /:settings/<tab>.
  const settingsPattern =
    /\/\:settings(?:\/(identity|navigation|web-domain|members|writers|email-subscribers|agents))?(?=[?#]|$)/
  const settingsMatch = inspectCleanUrl.match(settingsPattern)
  if (settingsMatch) {
    return {
      url: inspectCleanUrl.replace(settingsMatch[0], ''),
      isInspect,
      viewTerm: ':settings',
      settingsTab: settingsMatch[1],
    }
  }

  // Check for profile-family patterns like /:profile or /:profile/accountUid
  const profileFamilyPattern = /\/\:(profile|membership|followers|following)(?:\/([^/?#]+))?(?=[?#]|$)/
  const profileFamilyMatch = inspectCleanUrl.match(profileFamilyPattern)
  if (profileFamilyMatch) {
    return {
      url: inspectCleanUrl.replace(profileFamilyMatch[0], ''),
      isInspect,
      viewTerm: `:${profileFamilyMatch[1]}` as SiteProfileViewTerm,
      accountUid: profileFamilyMatch[2],
    }
  }

  for (const term of VIEW_TERMS) {
    // Match term at end of path (before query/fragment)
    const termPattern = new RegExp(`/${term.replace(':', '\\:')}(?=[?#]|$)`)
    if (termPattern.test(inspectCleanUrl)) {
      return {
        url: inspectCleanUrl.replace(termPattern, ''),
        isInspect,
        viewTerm: term,
      }
    }
  }
  return {url: inspectCleanUrl, isInspect, viewTerm: null}
}

/**
 * Convert view term to route key for navigation
 */
export function viewTermToRouteKey(viewTerm: ViewTerm | null): ViewRouteKey | null {
  if (!viewTerm) return null
  const mapping: Record<ViewTerm, ViewRouteKey> = {
    ':activity': 'activity',
    ':comments': 'comments',
    ':comment': 'comments', // backward compat
    ':discussions': 'comments', // backward compat
    ':collaborators': 'collaborators',
    ':directory': 'directory',
    ':feed': 'feed',
    ':all-documents': 'all-documents',
    ':explore': 'explore',
    ':settings': 'site-settings',
    ':attributes': 'metadata',
    ':metadata': 'metadata', // backward compat
    ':schema': 'schema',
    ':profile': 'profile',
    ':membership': 'membership',
    ':followers': 'followers',
    ':following': 'following',
  }
  return mapping[viewTerm] ?? null
}

export function isSiteProfileTab(value: string | null | undefined): value is SiteProfileTab {
  return !!value && (SITE_PROFILE_TABS as readonly string[]).includes(value)
}

/** Builds a site URL with optional view, panel, reply, and content selection. */
export function createSiteUrl({
  path,
  hostname,
  version,
  latest,
  blockRef,
  blockRange,
  viewTerm,
  panel,
  reply,
  pageParams,
}: {
  path: string[] | null | undefined
  hostname: string
  version?: string | null | undefined
  latest?: boolean
  blockRef?: string | null | undefined
  blockRange?: BlockRange | null
  viewTerm?: string | null
  panel?: string | null
  reply?: string | null
  pageParams?: Record<string, string>
}) {
  let res = hostname
  if (path && path.length) {
    res += '/' + path.join('/')
  }
  if (viewTerm) {
    res += '/' + viewTerm
  }
  res += getHMQueryString({latest, version, panel, reply, pageParams})
  if (blockRef) {
    res += `#${blockRef}${serializeBlockRange(blockRange)}`
  }
  return res
}

/**
 * Build a comment URL relative to a document context.
 * Produces site-style URLs when siteUrl is provided, gateway URLs otherwise.
 *
 * For `:comments` main view → `.../path/:comments/COMMENT_ID`
 * For document with panel   → `.../path?panel=comments/COMMENT_ID`
 * With blockRef             → append `#BLOCK_ID+` or `#BLOCK_ID[start:end]`
 */
export function createCommentUrl({
  docId,
  commentId,
  commentVersion,
  siteUrl,
  blockRef,
  blockRange,
  latest,
}: {
  docId: UnpackedHypermediaId
  commentId: string
  /** CID of the comment version to pin. When set, the URL resolves to this exact version. */
  commentVersion?: string | null
  siteUrl?: string | null
  blockRef?: string | null
  blockRange?: BlockRange | null
  latest?: boolean | null
}): string {
  const viewTermWithComment = `:comments/${commentId}`
  if (siteUrl) {
    return createSiteUrl({
      path: docId.path,
      pageParams: docId.pageParams,
      hostname: siteUrl,
      latest: latest ?? undefined,
      version: commentVersion ?? undefined,
      viewTerm: viewTermWithComment,
      blockRef,
      blockRange,
    })
  }
  return createWebHMUrl(docId.uid, {
    path: docId.path,
    pageParams: docId.pageParams,
    latest,
    version: commentVersion,
    viewTerm: viewTermWithComment,
    blockRef,
    blockRange,
  })
}

/**
 * Input for the unified copy-link builder. Describes the link target as an
 * `UnpackedHypermediaId` (document-like or a comment's containing document),
 * optionally with a separate `commentId: UnpackedHypermediaId` for the comment
 * itself. All other link parameters (version, blockRef, blockRange, latest,
 * hostname) are read from the respective ids.
 *
 * `id.hostname` acts as the site-vs-gateway flag: when set the URL is built in
 * site-style (`<hostname>/<path>`); when null/undefined it falls back to the
 * gateway-style `<gatewayUrl>/hm/<uid>/<path>` form. Callers with a custom
 * gateway (e.g. the desktop app's user-configurable gateway) can override the
 * default gateway via `gatewayUrl`.
 */
export type CopyLinkInput = {
  id: UnpackedHypermediaId
  commentId?: UnpackedHypermediaId | null
  /** Reply to select within the root identified by commentId. */
  replyCommentId?: string
  /** Layout to retain when copying a comment from a thread. */
  commentView?: 'panel' | 'expanded'
  gatewayUrl?: string | null
}

/**
 * Build a shareable URL for a document, block, fragment, comment, or a
 * block/fragment inside a comment — a single entry point that replaces the
 * scattered site/gateway/comment URL-building used for "Copy link" actions.
 *
 * Behavior:
 * - `input.id.hostname` set → site-style URL via `createSiteUrl`.
 * - `input.id.hostname` unset → gateway URL via `createWebHMUrl`.
 * - `input.commentId` present → opens the comment panel on the latest document.
 *   `commentView` retains the expanded layout and `replyCommentId` selects a reply.
 *   Comment block and fragment links retain the main comment route.
 *   Copied comment links do not pin document or comment versions.
 * - If `blockRef` is present on `input.id` and `blockRange` is `undefined`,
 *   default to `{expanded: true}` so whole-block links end in `+`. Callers may
 *   pass `{expanded: false}` or `{start,end}` to override.
 */
export function buildCopyLinkUrl(input: CopyLinkInput): string {
  const {id, commentId, gatewayUrl, replyCommentId, commentView} = input
  const effectiveRange: BlockRange | null | undefined =
    id.blockRef && (id.blockRange === undefined || id.blockRange === null) ? {expanded: true} : id.blockRange
  const siteHostname =
    id.hostname &&
    gatewayUrl &&
    /^https?:\/\/localhost(?::\d+)?(?:\/|$)/.test(gatewayUrl) &&
    id.hostname === 'localhost'
      ? gatewayUrl
      : id.hostname

  // Block and fragment links use the main comment route to retain their selection.
  if (commentId && id.blockRef) {
    return createCommentUrl({
      docId: id,
      commentId: `${commentId.uid}${commentId.path?.[0] ? `/${commentId.path[0]}` : ''}`,
      siteUrl: siteHostname,
      blockRef: id.blockRef,
      blockRange: effectiveRange,
      latest: id.latest,
    })
  }

  const commentPath = commentId ? `${commentId.uid}${commentId.path?.[0] ? `/${commentId.path[0]}` : ''}` : undefined
  const panel = commentPath && commentView !== 'expanded' ? `comments/${commentPath}` : undefined
  const viewTerm = commentPath && commentView === 'expanded' ? `:comments/${commentPath}` : undefined
  const reply = commentId ? replyCommentId : undefined

  if (siteHostname) {
    return createSiteUrl({
      path: id.path,
      pageParams: id.pageParams,
      hostname: siteHostname,
      version: commentId ? undefined : id.version,
      latest: id.latest ?? undefined,
      blockRef: id.blockRef,
      blockRange: effectiveRange,
      panel,
      viewTerm,
      reply,
    })
  }

  return createWebHMUrl(id.uid, {
    version: commentId ? undefined : id.version,
    blockRef: id.blockRef,
    blockRange: effectiveRange,
    panel,
    viewTerm,
    reply,
    latest: id.latest,
    path: id.path,
    pageParams: id.pageParams,
    hostname: gatewayUrl ?? undefined,
  })
}

export function getCommentTargetId(comment: HMComment | undefined): UnpackedHypermediaId | undefined {
  if (!comment) return undefined
  return hmId(comment.targetAccount, {
    path: entityQueryPathToHmIdPath(comment.targetPath || ''),
    version: comment.targetVersion,
  })
}

export function commentIdToHmId(commentId: string, version?: string | null): UnpackedHypermediaId {
  const commentIdParts = commentId.split('/')
  const uid = commentIdParts[0]!
  const tsid = commentIdParts[1]!
  return hmId(uid, {
    path: [tsid],
    version: version ?? undefined,
  })
}

export function hmIdToURL({uid, path, version, latest, blockRef, blockRange, pageParams}: UnpackedHypermediaId) {
  let res = `${HYPERMEDIA_SCHEME}://${uid}`

  if (path && path.length) {
    res += `/${path.join('/')}`
  }
  res += getHMQueryString({version, latest, pageParams})
  if (blockRef) {
    res += `#${blockRef}${serializeBlockRange(blockRange)}`
  }

  return res
}

/**
 * Create URL for OS protocol registration (to open desktop app).
 * Uses 'hm://'
 * This is separate from hmIdToURL which creates document URLs.
 */
export function createOSProtocolUrl({uid, path, version, latest, blockRef, blockRange}: UnpackedHypermediaId) {
  let res = `${OS_PROTOCOL_SCHEME}://${uid}`

  if (path && path.length) {
    res += `/${path.join('/')}`
  }
  res += getHMQueryString({version, latest})
  if (blockRef) {
    res += `#${blockRef}${serializeBlockRange(blockRange)}`
  }

  return res
}

function getInspectTargetPath(route: Extract<NavRoute, {key: 'inspect'}>): string {
  if (!route.targetView) return ''

  if (isSiteProfileTab(route.targetView)) {
    const accountSuffix = route.targetAccountUid ? `/${route.targetAccountUid}` : ''
    return `/:${route.targetView}${accountSuffix}`
  }

  if (route.targetView === 'activity') {
    const filterSlug = activityFilterToSlug(route.targetActivityFilter)
    return filterSlug ? `/:activity/${filterSlug}` : '/:activity'
  }

  if (route.targetView === 'comments') {
    return route.targetOpenComment ? `/:comments/${route.targetOpenComment}` : '/:comments'
  }

  return `/:${route.targetView}`
}

function getInspectQueryString({
  version,
  latest,
  inspectTab,
}: {
  version?: string | null
  latest?: boolean | null
  inspectTab?: string | null
}) {
  const query: Record<string, string | null> = {}
  if (version) {
    query.v = version
  }
  if (latest && version) {
    query.l = null
  }
  if (inspectTab && inspectTab !== 'document') {
    query.tab = inspectTab
  }
  return serializeQueryString(query)
}

function createWebInspectUrl(
  route: Extract<NavRoute, {key: 'inspect'}>,
  opts?: {
    hostname?: string | null | undefined
    originHomeId?: UnpackedHypermediaId | undefined
  },
) {
  const urlHost = opts?.hostname === undefined ? DEFAULT_GATEWAY_URL : opts?.hostname === null ? '' : opts.hostname
  const sameOriginSite = opts?.originHomeId?.uid === route.id.uid
  let res = sameOriginSite ? `${urlHost}/inspect` : `${urlHost}/hm/inspect/${route.id.uid}`
  if (route.id.path?.length) {
    res += `/${route.id.path.join('/')}`
  }
  res += getInspectTargetPath(route)
  res += getInspectQueryString({
    latest: route.id.latest,
    version: route.id.latest ? undefined : route.id.version,
    inspectTab: route.inspectTab,
  })
  if (route.id.blockRef) {
    res += `#${route.id.blockRef}${serializeBlockRange(route.id.blockRange)}`
  }
  return res
}

function createHmInspectUrl(route: Extract<NavRoute, {key: 'inspect'}>) {
  let res = `${HYPERMEDIA_SCHEME}://inspect/${route.id.uid}`
  if (route.id.path?.length) {
    res += `/${route.id.path.join('/')}`
  }
  res += getInspectTargetPath(route)
  res += getInspectQueryString({
    latest: route.id.latest,
    version: route.id.latest ? undefined : route.id.version,
    inspectTab: route.inspectTab,
  })
  if (route.id.blockRef) {
    res += `#${route.id.blockRef}${serializeBlockRange(route.id.blockRange)}`
  }
  return res
}

function createWebInspectIpfsUrl(
  route: Extract<NavRoute, {key: 'inspect-ipfs'}>,
  opts?: {
    hostname?: string | null | undefined
    originHomeId?: UnpackedHypermediaId | undefined
  },
) {
  const urlHost = opts?.hostname === undefined ? DEFAULT_GATEWAY_URL : opts?.hostname === null ? '' : opts.hostname
  const basePath = opts?.originHomeId ? `${urlHost}/inspect` : `${urlHost}/hm/inspect`
  return `${basePath}/ipfs/${route.ipfsPath}`
}

function createHmInspectIpfsUrl(route: Extract<NavRoute, {key: 'inspect-ipfs'}>) {
  return `${HYPERMEDIA_SCHEME}://inspect/ipfs/${route.ipfsPath}`
}
/**
 * Extract panel param from route for URL query param
 * Supports:
 * - "comments/COMMENT_ID" for specific comment open in panel
 * - "comments/BLOCKID" for block-specific comments
 * - "comments", "activity", etc. for general panels
 */
export function getRoutePanelParam(route: NavRoute): string | null {
  let panel:
    | {
        key: string
        targetBlockId?: string
        openComment?: string
      }
    | null
    | undefined = null

  if (route.key === 'document' && route.panel) {
    panel = route.panel
  } else if (
    (route.key === 'activity' ||
      route.key === 'comments' ||
      route.key === 'collaborators' ||
      route.key === 'directory' ||
      route.key === 'metadata' ||
      route.key === 'feed') &&
    route.panel
  ) {
    panel = route.panel
  }

  if (!panel || (route.key === 'comments' && panel.key === 'comments')) return null

  // Priority 1: Encode openComment - most specific
  if (panel.key === 'comments' && panel.openComment) {
    return `comments/${panel.openComment}`
  }

  // Priority 2: Encode targetBlockId into comments panel param
  if (panel.key === 'comments' && panel.targetBlockId) {
    return `comments/${panel.targetBlockId}`
  }

  // Encode activity filter slug into panel param
  if (panel.key === 'activity') {
    const filterSlug = activityFilterToSlug((panel as {filterEventType?: string[]}).filterEventType)
    if (filterSlug) return `activity/${filterSlug}`
  }

  // The space Activity panel spells out To you. All updates is the bare key
  if (panel.key === 'feed' && (panel as {tab?: string}).tab === 'to-you') {
    return 'feed/to-you'
  }

  return panel.key as PanelQueryKey
}

export function routeToUrl(
  route: NavRoute,
  opts?: {
    hostname?: string | null | undefined
    originHomeId?: UnpackedHypermediaId | undefined
  },
): string | null {
  const panelParam = getRoutePanelParam(route)

  if (route.key === 'inspect') {
    return createWebInspectUrl(route, opts)
  }
  if (route.key === 'inspect-ipfs') {
    return createWebInspectIpfsUrl(route, opts)
  }

  if (route.key === 'explore') {
    if (route.context.type !== 'site') return null
    const siteId = route.context.id
    return appendExploreQuery(
      createWebHMUrl(siteId.uid, {
        ...siteId,
        hostname: opts?.hostname,
        originHomeId: opts?.originHomeId,
        viewTerm: ':explore',
      }),
      route.q,
      route.sort,
    )
  }

  if (route.key === 'document') {
    const url = createWebHMUrl(route.id.uid, {
      ...route.id,
      hostname: opts?.hostname,
      originHomeId: opts?.originHomeId,
      panel: panelParam,
      reply: getRouteReplyCommentId(route),
      pageParams: route.id.pageParams,
    })
    return url
  }
  if (
    route.key === 'feed' ||
    route.key === 'activity' ||
    route.key === 'directory' ||
    route.key === 'collaborators' ||
    route.key === 'comments' ||
    route.key === 'all-documents' ||
    route.key === 'metadata'
  ) {
    // View-term routes use /:viewTerm in the path. The metadata view is
    // surfaced in URLs as :attributes (the route key stays `metadata`).
    let viewTermPath = route.key === 'metadata' ? ':attributes' : `:${route.key}`
    // Append activity filter slug to view term path
    if (route.key === 'activity') {
      const filterSlug = activityFilterToSlug(route.filterEventType)
      if (filterSlug) {
        viewTermPath = `:activity/${filterSlug}`
      }
    }
    if (route.key === 'feed' && route.tab === 'to-you') {
      viewTermPath = ':feed/to-you'
    }
    // For comments with openComment, put commentId in view term path.
    // On comment permalinks, ?v pins the comment version (not the document version).
    let commentPermalinkVersion: {version: string | null; latest: null} | undefined
    if (route.key === 'comments' && route.openComment) {
      viewTermPath = `:comments/${route.openComment}`
      commentPermalinkVersion = {version: route.openCommentVersion || null, latest: null}
    }
    const effectivePanelParam = route.key === 'all-documents' ? null : panelParam
    return createWebHMUrl(route.id.uid, {
      ...route.id,
      ...commentPermalinkVersion,
      hostname: opts?.hostname,
      originHomeId: opts?.originHomeId,
      viewTerm: viewTermPath,
      panel: effectivePanelParam,
      reply: getRouteReplyCommentId(route),
      pageParams: route.id.pageParams,
    })
  }
  if (route.key === 'schema' && route.id) {
    // The Schema tool tab: the document URL suffixed with /:schema.
    return createWebHMUrl(route.id.uid, {
      ...route.id,
      hostname: opts?.hostname,
      originHomeId: opts?.originHomeId,
      viewTerm: ':schema',
    })
  }
  if (route.key === 'site-settings') {
    const urlHost = opts?.hostname === undefined ? DEFAULT_GATEWAY_URL : opts?.hostname === null ? '' : opts.hostname
    const siteBase = opts?.originHomeId?.uid === route.id.uid ? '' : `/hm/${route.id.uid}`
    const tabPath = route.tab ? `/${route.tab}` : ''
    const domainPath = route.tab === 'web-domain' && route.domain ? `/${encodeURIComponent(route.domain)}` : ''
    return `${urlHost}${siteBase}/:settings${tabPath}${domainPath}`
  }
  if (route.key === 'site-profile') {
    const urlHost = opts?.hostname === undefined ? DEFAULT_GATEWAY_URL : opts?.hostname === null ? '' : opts.hostname
    const siteBase = opts?.originHomeId?.uid === route.id.uid ? '' : `/hm/${route.id.uid}`
    const accountSuffix = route.accountUid && route.accountUid !== route.id.uid ? `/${route.accountUid}` : ''
    return `${urlHost}${siteBase}/:${route.tab}${accountSuffix}`
  }
  if (route.key === 'profile') {
    const urlHost = opts?.hostname === undefined ? DEFAULT_GATEWAY_URL : opts?.hostname === null ? '' : opts.hostname
    const siteBase = opts?.originHomeId?.uid === route.id.uid ? '' : `/hm/${route.id.uid}`
    const tab = route.tab || 'profile'
    return `${urlHost}${siteBase}/:${tab}`
  }
  if (route.key === 'site-settings-emails') {
    if (!route.accountUid) return null
    const urlHost = opts?.hostname === undefined ? DEFAULT_GATEWAY_URL : opts?.hostname === null ? '' : opts.hostname
    const siteBase = opts?.originHomeId?.uid === route.accountUid ? '' : `/hm/${route.accountUid}`
    return `${urlHost}${siteBase}/:settings/email-subscribers`
  }
  if (route.key === 'contact') {
    const urlHost = opts?.hostname ?? DEFAULT_GATEWAY_URL
    return `${urlHost}/hm/contact/${route.id.uid}`
  }
  return null
}

/** Gets the continuation target from the single active Comments view. */
export function getRouteReplyCommentId(route: NavRoute): string | undefined {
  if (route.key === 'comments') return route.openComment ? route.replyCommentId : undefined
  if ('panel' in route && route.panel?.key === 'comments' && route.panel.openComment) {
    return route.panel.replyCommentId
  }
  return undefined
}

/**
 * Serializes an application route into an hm:// URL.
 */
export function routeToHmUrl(route: NavRoute): string | null {
  const panelParam = getRoutePanelParam(route)

  if (route.key === 'inspect') {
    return createHmInspectUrl(route)
  }
  if (route.key === 'inspect-ipfs') {
    return createHmInspectIpfsUrl(route)
  }

  if (route.key === 'explore') {
    if (route.context.type !== 'site') return null
    const siteId = route.context.id
    return appendExploreQuery(`${packBaseId(siteId.uid, siteId.path)}/:explore`, route.q, route.sort)
  }

  if (route.key === 'document') {
    let url = packBaseId(route.id.uid, route.id.path)
    url += getHMQueryString({
      version: route.id.version,
      latest: route.id.latest,
      panel: panelParam,
      reply: getRouteReplyCommentId(route),
      pageParams: route.id.pageParams,
    })
    if (route.id.blockRef) {
      url += `#${route.id.blockRef}${serializeBlockRange(route.id.blockRange)}`
    }
    return url
  }

  if (
    route.key === 'feed' ||
    route.key === 'activity' ||
    route.key === 'directory' ||
    route.key === 'collaborators' ||
    route.key === 'comments' ||
    route.key === 'all-documents' ||
    route.key === 'metadata'
  ) {
    let viewTermPath = route.key === 'metadata' ? ':attributes' : `:${route.key}`
    if (route.key === 'activity') {
      const filterSlug = activityFilterToSlug(route.filterEventType)
      if (filterSlug) viewTermPath = `:activity/${filterSlug}`
    }
    if (route.key === 'feed' && route.tab === 'to-you') viewTermPath = ':feed/to-you'
    // On comment permalinks, ?v pins the comment version (not the document version).
    let versionQuery: {version?: string | null; latest?: boolean | null} = {
      version: route.id.version,
      latest: route.id.latest,
    }
    if (route.key === 'comments' && route.openComment) {
      viewTermPath = `:comments/${route.openComment}`
      versionQuery = {version: route.openCommentVersion || null}
    }

    let url = packBaseId(route.id.uid, route.id.path)
    url += `/${viewTermPath}`
    url += getHMQueryString({
      ...versionQuery,
      panel: panelParam,
      reply: getRouteReplyCommentId(route),
      pageParams: route.id.pageParams,
    })
    if (route.id.blockRef) {
      url += `#${route.id.blockRef}${serializeBlockRange(route.id.blockRange)}`
    }
    return url
  }

  if (route.key === 'site-settings') {
    const tabPath = route.tab ? `/${route.tab}` : ''
    const domainPath = route.tab === 'web-domain' && route.domain ? `/${encodeURIComponent(route.domain)}` : ''
    return `${packBaseId(route.id.uid, route.id.path)}/:settings${tabPath}${domainPath}`
  }

  // The schema a document defines: the document URL suffixed with /:schema.
  if (route.key === 'schema' && route.id) {
    return `${packBaseId(route.id.uid, route.id.path)}/:schema`
  }

  if (route.key === 'site-profile') {
    const accountSuffix = route.accountUid && route.accountUid !== route.id.uid ? `/${route.accountUid}` : ''
    return `${packBaseId(route.id.uid)}/:${route.tab}${accountSuffix}`
  }

  if (route.key === 'profile') {
    return `${packBaseId(route.id.uid)}/:${route.tab || 'profile'}`
  }

  return null
}

/** Build a bookmark URL from a NavRoute, keeping only baseId + view term. */
export function bookmarkUrlFromRoute(route: NavRoute): string | null {
  if (route.key === 'inspect') {
    return createHmInspectUrl(route)
  }
  if (route.key === 'inspect-ipfs') {
    return createHmInspectIpfsUrl(route)
  }
  if (route.key === 'document') {
    return packBaseId(route.id.uid, route.id.path)
  }
  if (
    route.key === 'feed' ||
    route.key === 'activity' ||
    route.key === 'directory' ||
    route.key === 'collaborators' ||
    route.key === 'comments' ||
    route.key === 'all-documents' ||
    route.key === 'metadata'
  ) {
    const term = route.key === 'metadata' ? 'attributes' : route.key
    return `${packBaseId(route.id.uid, route.id.path)}/:${term}`
  }
  if (route.key === 'site-settings') {
    return `${packBaseId(route.id.uid, route.id.path)}/:settings`
  }
  if (route.key === 'profile') {
    return `${packBaseId(route.id.uid)}/:${route.tab || 'profile'}`
  }
  if (route.key === 'site-profile') {
    const accountSuffix = route.accountUid && route.accountUid !== route.id.uid ? `/${route.accountUid}` : ''
    return `${packBaseId(route.id.uid)}/:${route.tab}${accountSuffix}`
  }
  return null
}

export function createWebHMUrl(
  uid: string,
  {
    version,
    blockRef,
    blockRange,
    hostname,
    latest,
    path,
    originHomeId,
    viewTerm,
    panel,
    reply,
    pageParams,
  }: {
    pageParams?: Record<string, string>
    version?: string | null | undefined
    blockRef?: string | null | undefined
    blockRange?: BlockRange | null
    hostname?: string | null | undefined
    latest?: boolean | null
    path?: string[] | null
    originHomeId?: UnpackedHypermediaId
    viewTerm?: string | null
    panel?: string | null
    reply?: string | null
  } = {},
) {
  let webPath = `/hm/${uid}`
  if (originHomeId?.uid === uid) {
    webPath = ''
  }
  const urlHost = hostname === undefined ? DEFAULT_GATEWAY_URL : hostname === null ? '' : hostname
  let res = `${urlHost}${webPath}`
  if (path && path.length) {
    res += `/${path.join('/')}`
  }
  if (viewTerm) {
    res += `/${viewTerm}`
  }
  if (res === '') res = '/'
  res += getHMQueryString({
    latest: latest ?? null,
    version: latest ? undefined : version,
    panel,
    reply,
    pageParams,
  })
  if (blockRef) {
    res += `#${blockRef}${serializeBlockRange(blockRange)}`
  }
  return res
}

function getHMQueryString({
  version,
  latest,
  panel,
  reply,
  pageParams,
}: {
  pageParams?: Record<string, string>
  version?: string | null
  latest?: boolean | null
  panel?: string | null
  reply?: string | null
}) {
  const query: Record<string, string | null> = {}
  if (version) {
    query.v = version
  }
  if (latest && version) {
    query.l = null
  }
  if (panel) {
    query.panel = panel
  }
  if (reply) query.reply = reply
  const base = serializeQueryString(query)
  const input = pageParamsQuery(pageParams)
  return base + (input ? (base ? '&' : '?') + input : '')
}

function packBaseId(uid: string, path?: string[] | null) {
  const filteredPath = path?.filter((p) => p !== '') || []
  const restPath = filteredPath.length ? `/${filteredPath.join('/')}` : ''
  return `${HYPERMEDIA_SCHEME}://${uid}${restPath}`
}

export function packHmId(hmId: UnpackedHypermediaId): string {
  const {path, version, latest, blockRef, blockRange, uid, pageParams} = hmId
  if (!uid) throw new Error('uid is required')
  let responseUrl = packBaseId(uid, path)
  responseUrl += getHMQueryString({
    version,
    latest,
    pageParams,
  })
  if (blockRef) {
    responseUrl += `#${blockRef}${serializeBlockRange(blockRange)}`
  }
  return responseUrl
}

/**
 * Serializes a reference target using the canonical account/profile URL.
 * Root site documents are referenced through `/:profile` so references still
 * resolve when the account has no home document.
 */
export function packReferenceUrl(hmId: UnpackedHypermediaId): string {
  return packHmId({
    ...hmId,
    path: hmId.path?.length ? hmId.path : [':profile'],
  })
}

/** @deprecated Import from `@seed-hypermedia/client/hm-types` instead. */
export const parseCustomURL = _parseCustomURL

// this is used to convert an object that is a superset of HMId to an exact HMId. This is used in the case of embed props (but maybe we should reconsider this approach of spreading id directly into embed props)
export function hmId(
  idPath: string | null | undefined,
  opts: {
    version?: string | null
    blockRef?: string | null
    blockRange?: BlockRange | null
    path?: string[] | null
    latest?: boolean | null
    hostname?: string | null
    pageParams?: Record<string, string>
  } = {},
): UnpackedHypermediaId {
  const [uid, ...path] = (idPath || '').split('/')
  const effectivePath = opts.path || path || null
  return {
    ...opts,
    uid: uid || '',
    id: uid ? packBaseId(uid, effectivePath) : '',
    path: effectivePath,
    version: opts.version || null,
    blockRef: opts.blockRef || null,
    blockRange: opts.blockRange || null,
    hostname: opts.hostname || null,
    scheme: null,
  }
}

/** @deprecated Import from `@seed-hypermedia/client/hm-types` instead. */
export function unpackHmId(urlMaybe?: string | null): UnpackedHypermediaId | null {
  if (!urlMaybe) return null

  const unpacked = _unpackHmId(urlMaybe)
  const {commentId} = extractViewTermFromUrl(urlMaybe)
  if (!commentId) return unpacked

  const commentResourceId = commentIdToHmId(commentId, unpacked?.version)
  return hmId(commentResourceId.uid, {
    path: commentResourceId.path,
    version: commentResourceId.version,
    latest: unpacked?.latest,
    blockRef: unpacked?.blockRef,
    blockRange: unpacked?.blockRange,
    hostname: unpacked?.hostname,
  })
}

export function isHypermediaScheme(url?: string) {
  return !!url?.startsWith(`${HYPERMEDIA_SCHEME}://`) || !!url?.startsWith('hm://')
}

export function isPublicGatewayLink(text: string, gwUrl?: StateStream<string>) {
  const gateway = gwUrl?.get()
  if (!gateway) return false
  return text.indexOf(gateway) === 0
}

export function idToUrl(
  hmId: UnpackedHypermediaId,
  opts?: {
    originHomeId?: UnpackedHypermediaId
    panel?: string | null
  },
) {
  return createWebHMUrl(hmId.uid, {
    version: hmId.version,
    blockRef: hmId.blockRef,
    blockRange: hmId.blockRange,
    path: hmId.path,
    hostname: hmId.hostname,
    originHomeId: opts?.originHomeId,
    panel: opts?.panel,
    latest: hmId.latest,
  })
}

export function normalizeHmId(urlMaybe: string, gwUrl: StateStream<string>): string | undefined {
  if (isHypermediaScheme(urlMaybe)) return urlMaybe
  if (isPublicGatewayLink(urlMaybe, gwUrl)) {
    const unpacked = unpackHmId(urlMaybe)
    if (unpacked?.uid) {
      return packHmId(
        hmId(unpacked.uid, {
          path: unpacked.path,
          blockRange: unpacked.blockRange,
          blockRef: unpacked.blockRef,
          version: unpacked.version,
          latest: unpacked.latest,
        }),
      )
    }
    return undefined
  } else {
    return undefined
  }
}

function serializeQueryString(query: Record<string, string | null>) {
  const queryString = Object.entries(query)
    .map(([key, value]) => (value === null ? key : `${key}=${value}`))
    .join('&')
  if (!queryString) return ''
  return `?${queryString}`
}

function appendExploreQuery(url: string, q?: string | null, sort?: string | null) {
  const params = new URLSearchParams()
  if (q) params.set('q', q)
  if (sort) params.set('sort', sort)
  const query = params.toString()
  if (!query) return url
  return `${url}${url.includes('?') ? '&' : '?'}${query}`
}

/** @deprecated Import from `@seed-hypermedia/client/hm-types` instead. */
export const parseFragment = _parseFragment

export function serializeBlockRange(range: BlockRange | null | undefined): string {
  let res = ''
  if (range) {
    if ('expanded' in range && range.expanded) {
      res += '+'
    } else if ('start' in range) {
      res += `[${range.start}:${range.end}]`
    }
  }

  return res
}

export function displayHostname(fullHost: string): string {
  return fullHost.replace(/https?:\/\//, '')
}

/**
 * Splits a version string into its constituent head CIDs.
 *
 * A version is a dot-separated list of CIDs (one per concurrent head). When the document
 * has more than one head, the version becomes `cid1.cid2[.cid3...]`. This helper is the
 * single source of truth for "how many heads is this version" so UI code can detect the
 * merged/concurrent state without re-implementing the split.
 */
export function getVersionHeads(version: string | null | undefined): string[] {
  if (!version) return []
  return version.split('.').filter(Boolean)
}

export function hmIdMatches(a: UnpackedHypermediaId, b: UnpackedHypermediaId) {
  return (
    // @ts-expect-error
    a.type === b.type && a.uid === b.uid && a.version == b.version && pathMatches(a.path, b.path)
  )
}

export function pathMatches(a: string[] | null, b: string[] | null | undefined) {
  // Handle cases where either value is null/undefined and the other is empty array
  if (!a && (!b || b.length === 0)) return true
  if (!b && (!a || a.length === 0)) return true

  // Handle regular array comparison
  if (!a?.length || !b?.length) return a?.length === b?.length
  return a.length === b.length && a.every((v, i) => v === b[i])
}

export function isIdParentOfOrEqual(parent: UnpackedHypermediaId, possibleChild: UnpackedHypermediaId) {
  if (parent.uid !== possibleChild.uid) return false
  if (!parent.path?.length && !possibleChild.path?.length) return true
  if (!parent.path) return true
  return parent.path?.every((v, i) => v === possibleChild.path?.[i])
}

export function isPathParentOfOrEqual(parentPath?: string[] | null, possibleChildPath?: string[] | null) {
  if (!parentPath?.length && !possibleChildPath?.length) return true
  if (!parentPath) return true
  return parentPath?.every((v, i) => v === possibleChildPath?.[i])
}

export function latestId(id: UnpackedHypermediaId): UnpackedHypermediaId {
  return {
    ...id,
    latest: true,
    version: null,
  }
}

export function getParent(id: UnpackedHypermediaId | null | undefined): UnpackedHypermediaId | null {
  if (!id) return null
  const parentPath = id.path?.slice(0, -1) || []
  return hmId(id.uid, {
    path: parentPath,
  })
}
